/**
 * CollapsiblePanel - Panel with collapsible content
 * Persists collapsed state in localStorage
 */

import { useState, useEffect, ReactNode } from 'react';
import './ui.css';

interface CollapsiblePanelProps {
  id: string;
  title: string;
  children: ReactNode;
  defaultCollapsed?: boolean;
  variant?: 'default' | 'primary' | 'secondary';
  className?: string;
}

const STORAGE_KEY_PREFIX = 'genesis_panel_collapsed_';

export function CollapsiblePanel({
  id,
  title,
  children,
  defaultCollapsed = false,
  variant = 'default',
  className = '',
}: CollapsiblePanelProps) {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}${id}`);
    return stored !== null ? stored === 'true' : defaultCollapsed;
  });

  useEffect(() => {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${id}`, String(isCollapsed));
  }, [id, isCollapsed]);

  const toggleCollapsed = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <section
      className={`panel collapsible-panel ${variant !== 'default' ? `panel-${variant}` : ''} ${isCollapsed ? 'collapsed' : ''} ${className}`}
    >
      <button
        className="collapsible-header"
        onClick={toggleCollapsed}
        aria-expanded={!isCollapsed}
        aria-controls={`panel-content-${id}`}
      >
        <h3>{title}</h3>
        <span className={`chevron ${isCollapsed ? 'collapsed' : ''}`} aria-hidden="true">
          &#9660;
        </span>
      </button>
      <div
        id={`panel-content-${id}`}
        className={`collapsible-content ${isCollapsed ? 'hidden' : ''}`}
      >
        {children}
      </div>
    </section>
  );
}
